import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Printer, AlertTriangle, ArrowLeft, Star, Save } from "lucide-react";

/**
 * Posse Past-Due — a printable "5-10 cars stuck" watchlist.
 * Shows every used vehicle >7 days in recon with an auto reason plus an
 * optional manager note. Prints cleanly on 8.5x11 in a single dense sheet.
 */

const BRAND_BRONZE = "#a06a1e";
const BRAND_BRONZE_DARK = "#7a4e0f";
const BRAND_CREAM = "#f6ecd8";

function money(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);
}

export default function PossePastDuePage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState({}); // vehicle_id -> draft note
  const [saving, setSaving] = useState({}); // vehicle_id -> bool

  const load = () => {
    setBusy(true);
    return api
      .get("/posse/past-due")
      .then((r) => {
        setData(r.data);
        // seed note drafts with any existing note text
        const seed = {};
        for (const v of r.data.vehicles || []) {
          if (v.manager_note) seed[v.id] = v.manager_note;
        }
        setNotes(seed);
      })
      .catch((e) => toast.error(formatError(e.response?.data?.detail)))
      .finally(() => setBusy(false));
  };
  useEffect(() => {
    load();
  }, []);

  const saveNote = async (vid) => {
    const note = (notes[vid] || "").trim();
    if (!note) {
      toast.error("Type a note first.");
      return;
    }
    setSaving((s) => ({ ...s, [vid]: true }));
    try {
      await api.post("/posse/past-due-note", { vehicle_id: vid, note });
      toast.success("Note saved");
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving((s) => ({ ...s, [vid]: false }));
    }
  };

  const vehicles = data?.vehicles || [];
  const dealer = data?.dealership_name || "Your Dealership";

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Recon Recovery — Cars > 7 Days in Recon" />

      {/* Brand strip — hidden on print */}
      <div
        className="mb-5 rounded-xl overflow-hidden shadow-xl relative no-print"
        style={{
          background: `linear-gradient(135deg, ${BRAND_BRONZE_DARK} 0%, ${BRAND_BRONZE} 60%, #c98c3d 100%)`,
        }}
        data-testid="posse-past-due-hero"
      >
        <div className="relative p-5 md:p-6 flex items-center gap-4 flex-wrap">
          <div
            className="w-14 h-14 rounded-full grid place-items-center shadow-xl shrink-0"
            style={{ background: BRAND_CREAM }}
          >
            <AlertTriangle
              className="w-7 h-7"
              strokeWidth={2.5}
              style={{ color: BRAND_BRONZE_DARK }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono-label text-[0.6rem] text-white/85 tracking-widest">
              PREOWNED PIPELINE · DELAYED VEHICLES
            </div>
            <div className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white leading-none mt-1">
              Delayed Vehicles
            </div>
            <div className="font-display text-white/90 text-sm md:text-base mt-1">
              {vehicles.length} car{vehicles.length === 1 ? "" : "s"} over 7 days in recon — with the reason it's stuck and a manager note.
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/posse">
              <Button
                variant="outline"
                className="bg-white/95 hover:bg-white text-neutral-900 border-transparent"
                data-testid="posse-past-due-back"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
                Back to Pipeline
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={() => window.print()}
              data-testid="posse-past-due-print"
              className="bg-white/95 hover:bg-white text-neutral-900 border-transparent"
            >
              <Printer className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
              Print
            </Button>
          </div>
        </div>
      </div>

      {/* Print-only header — visible only when printing */}
      <div className="hidden print:block mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Star className="w-4 h-4" style={{ color: BRAND_BRONZE_DARK }} />
          <div className="font-mono text-[0.65rem] tracking-widest">
            {dealer.toUpperCase()} · PREOWNED PIPELINE · DELAYED VEHICLES
          </div>
        </div>
        <div className="font-display font-black text-xl">Cars &gt; 7 Days in Recon</div>
        <div className="text-[0.65rem] text-neutral-600">
          Generated {new Date().toLocaleString()} · {vehicles.length} entries
        </div>
      </div>

      {busy && !data && (
        <div className="text-sm text-muted-foreground p-4">Loading past-due list…</div>
      )}

      {!busy && vehicles.length === 0 && (
        <Card className="p-8 text-center border-2 border-emerald-300 bg-emerald-50/60">
          <div className="text-emerald-800 font-display font-black text-2xl">
            🟢 Pipeline is clean.
          </div>
          <div className="text-sm text-emerald-800/80 mt-2">
            No used vehicles have been in recon longer than 7 days.
          </div>
        </Card>
      )}

      {vehicles.length > 0 && (
        <div className="rounded-lg border border-border overflow-hidden overflow-x-auto" data-testid="posse-past-due-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead
              className="text-left"
              style={{ background: BRAND_CREAM, color: BRAND_BRONZE_DARK }}
            >
              <tr className="font-mono-label text-[0.6rem] tracking-widest">
                <th className="p-2">Vehicle</th>
                <th className="p-2 text-right">Days</th>
                <th className="p-2">Why It's Delayed</th>
                <th className="p-2">Manager</th>
                <th className="p-2 hidden lg:table-cell">Stage</th>
                <th className="p-2 hidden lg:table-cell">Dates</th>
                <th className="p-2 hidden xl:table-cell">Lights</th>
                <th className="p-2 no-print min-w-[12rem]">Manager Note</th>
                <th className="p-2 hidden print:table-cell">Note</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.map((v) => (
                <tr
                  key={v.id}
                  className="border-t border-border align-top hover:bg-muted/30"
                  data-testid={`posse-past-due-row-${v.id}`}
                >
                  <td className="p-2 whitespace-nowrap">
                    <div className="font-semibold">
                      {v.year} {v.make_model}
                    </div>
                    <div className="font-mono text-[0.65rem] text-neutral-500">
                      Stock {v.stock_no} · VIN …{v.vin_last6}
                    </div>
                  </td>
                  <td className="p-2 text-right tabular-nums font-bold text-red-700 text-base">
                    {v.days_in_recon}d
                  </td>
                  <td className="p-2 text-[0.8rem] max-w-[18rem]">
                    <div className="font-semibold text-red-700 leading-tight">
                      {v.stuck_reason}
                    </div>
                    {v.manager_note && (
                      <div className="mt-1 text-[0.65rem] text-neutral-700 italic border-l-2 border-amber-500 pl-2">
                        “{v.manager_note}”
                        {v.manager_note_by && (
                          <span className="text-neutral-500 not-italic">
                            {" "}
                            — {v.manager_note_by}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-2 whitespace-nowrap">{v.responsible_manager}</td>
                  <td className="p-2 capitalize hidden lg:table-cell">{(v.status || "").replace("_", " ")}</td>
                  <td className="p-2 hidden lg:table-cell font-mono text-[0.65rem] text-neutral-600 whitespace-nowrap">
                    <div>Acq: {v.acquired_on}</div>
                    <div>Target: {v.target_frontline_date}</div>
                  </td>
                  <td className="p-2 hidden xl:table-cell whitespace-nowrap">
                    <span className="inline-flex gap-1 items-center">
                      {(v.status_lights || []).map((l) => (
                        <span
                          key={l.key}
                          title={`${l.label}: ${l.green ? "green" : "red"}`}
                          className={`inline-block w-2.5 h-2.5 rounded-full ${
                            l.green ? "bg-emerald-500" : "bg-red-500 animate-pulse"
                          }`}
                          style={{ border: "1px solid rgba(0,0,0,0.4)" }}
                          aria-hidden
                        />
                      ))}
                    </span>
                  </td>
                  {/* Editable note input (screen only) */}
                  <td className="p-2 no-print min-w-[14rem]">
                    <Textarea
                      value={notes[v.id] || ""}
                      onChange={(e) =>
                        setNotes((n) => ({ ...n, [v.id]: e.target.value }))
                      }
                      placeholder="What's blocking it?"
                      rows={2}
                      className="text-xs"
                      data-testid={`posse-past-due-note-${v.id}`}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => saveNote(v.id)}
                      disabled={saving[v.id]}
                      className="mt-1 h-7 text-xs"
                      data-testid={`posse-past-due-save-${v.id}`}
                    >
                      <Save className="w-3 h-3 mr-1" strokeWidth={2} />
                      {saving[v.id] ? "Saving…" : "Save note"}
                    </Button>
                  </td>
                  {/* Print-only note column */}
                  <td className="p-2 hidden print:table-cell text-[0.7rem] max-w-[10rem]">
                    {v.manager_note || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer / legend */}
      {vehicles.length > 0 && (
        <div className="text-[0.7rem] text-muted-foreground mt-3 leading-relaxed max-w-3xl">
          <b>Rule:</b> anything &gt;7 days in recon lands here — auto reason is generated
          from open recon items and target front-line date. Managers can append a note
          explaining the block; those notes ride along on the GM's nightly digest.
        </div>
      )}
    </DashboardShell>
  );
}
